import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import api from "../../utils/api";
import { useDriveView, type DriveViewVote } from "./driveViewStore";

// Encuesta de la beta (provisional, Daniel 29-oct): al cerrar la vista la
// primera vez, «¿te gustó?». Sí / No suman a su total; cerrar sin contestar
// (✕, Esc o clic fuera) cuenta como «meh». El voto va al API, que guarda quién
// votó (usuario, compañía y rol) en beta_feedback/drive_view.
function sendVote(vote: DriveViewVote) {
  // Si falla no se reintenta ni se avisa: es una encuesta, no debe estorbar.
  api.post("/beta-feedback/drive_view", { vote }).catch((err) => console.warn("[driveView] voto no enviado", err));
}

/**
 * Modal centrado sobre toda la plataforma, con fondo oscurecido. En una esquina
 * del mapa parecía un panel olvidado: se podía seguir usando todo y no se iba
 * (Daniel, 29-oct).
 */
export default function DriveFeedback({ isDarkMode }: { isDarkMode: boolean }) {
  const { t } = useTranslation("lite");
  const answer = useDriveView((s) => s.answer);
  const yesRef = useRef<HTMLButtonElement>(null);
  const vote = (v: DriveViewVote) => sendVote(answer(v));

  useEffect(() => {
    yesRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") vote("meh");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = t("driveView.feedbackTitle", "¿Te gustó la vista de manejo?");
  const choice = "flex-1 h-11 rounded-xl text-sm font-semibold transition-[background-color,transform] duration-150 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500";
  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px] motion-safe:animate-fadeIn"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) vote("meh");
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative w-full max-w-[340px] rounded-2xl p-5 shadow-2xl ring-1 motion-safe:animate-scaleIn ${
          isDarkMode ? "bg-dark-surface text-white ring-white/10" : "bg-white text-gray-900 ring-black/5"
        }`}
      >
        <button
          type="button"
          onClick={() => vote("meh")}
          aria-label={t("driveView.feedbackSkip", "Cerrar sin contestar")}
          title={t("driveView.feedbackSkip", "Cerrar sin contestar")}
          className={`absolute right-3 top-3 h-8 w-8 flex items-center justify-center rounded-lg ${isDarkMode ? "text-white/60 hover:bg-white/10 hover:text-white" : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"}`}
        >
          <i className="ri-close-line text-lg" aria-hidden />
        </button>
        <div className="mb-3 flex items-center gap-2">
          <i className="ri-gamepad-line text-2xl text-primary-500" aria-hidden />
          <span className="rounded bg-primary-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-900">
            {t("driveView.beta", "Beta")}
          </span>
        </div>
        <p className="text-base font-semibold leading-snug">{title}</p>
        <p className={`mt-1 text-sm ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>
          {t("driveView.feedbackHint", "Es una función nueva y tu opinión nos dice si la dejamos.")}
        </p>
        <div className="mt-4 flex gap-2">
          <button ref={yesRef} type="button" onClick={() => vote("like")} className={`${choice} bg-primary-500 text-gray-900 hover:bg-primary-400`}>
            👍 {t("driveView.feedbackYes", "Sí")}
          </button>
          <button
            type="button"
            onClick={() => vote("dislike")}
            className={`${choice} ${isDarkMode ? "bg-white/10 hover:bg-white/15" : "bg-gray-100 hover:bg-gray-200"}`}
          >
            👎 {t("driveView.feedbackNo", "No")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
